import React from "react";
import { useLang } from "@/i18n/LanguageContext";
import { Eye, Flower2, Salad, Utensils } from "lucide-react";
import { PRE_HARVEST_MODE } from "@/lib/siteMode";

const ICONS = [Eye, Flower2, Utensils, Salad];

/**
 * Editorial tasting guide — sight, nose, palate, finish + pairings.
 * Positioned after the Culinary section to reinforce sensory value.
 */
export default function TastingNotes() {
  const { t } = useLang();
  const notes = t("tasting.notes");
  const pairings = t("tasting.pairings");

  return (
    <section
      id="cata"
      data-testid="tasting-notes-section"
      className="relative bg-cream-100 py-24 sm:py-32"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-12 lg:mb-16">
          <div className="lg:col-span-7">
            <span className="text-[11px] uppercase tracking-[0.32em] text-gold-deep font-medium">
              {t("tasting.overline")}
            </span>
            <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] text-olive-900 font-medium">
              {t("tasting.title_a")} <span className="italic">{t("tasting.title_b")}</span>
              {t("tasting.title_c")}
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-base sm:text-lg text-olive-800/80 font-light leading-relaxed">
              {t("tasting.body")}
            </p>
            {PRE_HARVEST_MODE && (
              <p
                data-testid="tasting-reference-note"
                className="mt-4 text-xs text-olive-800/55 border-l-2 border-gold-deep pl-3 italic"
              >
                {t("pre_harvest.reference_note")}
              </p>
            )}
          </div>
        </div>

        {/* Notes grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 border-t border-olive-700/15">
          {notes.map((n, i) => {
            const Icon = ICONS[i] || Eye;
            return (
              <div
                key={n.label}
                data-testid={`tasting-note-${i}`}
                className={`p-8 lg:p-10 border-b border-olive-700/15 ${
                  i > 0 ? "md:border-l" : ""
                } ${i === 2 ? "lg:border-l md:border-l-0" : ""} ${
                  i === 3 ? "lg:border-l" : ""
                } border-olive-700/15`}
              >
                <Icon size={22} strokeWidth={1.2} className="text-gold-deep" />
                <div className="mt-6 text-[10px] uppercase tracking-[0.3em] text-olive-800/60">
                  0{i + 1}
                </div>
                <h3 className="mt-2 font-serif text-2xl text-olive-900">{n.label}</h3>
                <p className="mt-3 text-olive-800/80 font-light leading-relaxed">{n.text}</p>
              </div>
            );
          })}
        </div>

        {/* Pairings */}
        <div className="mt-16 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-4">
            <span className="text-[11px] uppercase tracking-[0.32em] text-gold-deep font-medium">
              {t("tasting.pairings_title")}
            </span>
          </div>
          <ul className="lg:col-span-8 grid sm:grid-cols-2 gap-x-8 gap-y-3">
            {pairings.map((p) => (
              <li
                key={p}
                className="flex items-start gap-3 py-3 border-t border-cream-400 text-olive-800/90 font-light"
              >
                <span className="mt-2 w-1.5 h-1.5 bg-gold-deep rounded-full shrink-0" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
